import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ShieldCheck, Calendar, Users, Wrench, FileText, Box,
  BarChart3, MessageSquare, LayoutDashboard, Cloud, DatabaseBackup, Palette,
  Plug, Smartphone, Star, ChevronDown, Menu, X, Bell, Search, TrendingUp,
  Sun, Moon, Sparkles, CreditCard, Clock, Lock, MonitorSmartphone, Building2,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const NAV = [
  { key: "nav.features", href: "#funktioner" },
  { key: "steps.title", href: "#sa-fungerar" },
  { key: "nav.pricing", href: "#priser" },
  { key: "faq.title", href: "#faq" },
];
const FEATURE_ICONS = [Calendar, Users, Wrench, FileText, Box, LayoutDashboard, BarChart3, MessageSquare];
const WHY_ICONS = [Cloud, DatabaseBackup, Palette, Plug, Users, Smartphone];
const TRUST_ICONS = [ShieldCheck, Lock, DatabaseBackup, Palette, MonitorSmartphone];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground antialiased" data-testid="landing-page">
      <Background />
      <Nav />
      <main className="relative z-10">
        <Hero />
        <TrustBar />
        <Clients />
        <Features />
        <WhyUs />
        <HowItWorks />
        <Testimonials />
        <Pricing />
        <Faq />
        <CtaBand />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Multi-layer premium background ---------- */
function Background() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {/* static mesh glow blobs (kept cheap: no animation, smaller blur) */}
      <div className="absolute -left-40 -top-56 h-[560px] w-[560px] rounded-full opacity-25 blur-[90px] dark:opacity-40" style={{ background: "hsl(var(--primary))" }} />
      <div className="absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[#7C3AED] opacity-[0.16] blur-[100px] dark:opacity-25" />
      {/* subtle grid */}
      <div className="absolute inset-0 lp-grid" />
      {/* top spotlight glow + edge vignette */}
      <div className="absolute left-1/2 top-[-6%] h-[460px] w-[900px] -translate-x-1/2 rounded-full blur-[110px]" style={{ background: "radial-gradient(ellipse at center, hsl(var(--primary) / 0.2), transparent 70%)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 85% 65% at 50% 100%, hsl(var(--background) / 0.92), transparent 60%)" }} />
    </div>
  );
}

function Logo({ footer = false }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
      <span className={`${footer ? "h-8 w-8" : "h-10 w-10"} overflow-hidden rounded-xl ring-1 ring-border`}><BrandLogo className="h-full w-full" /></span>
      <span className="font-display text-xl font-bold tracking-tight">ArcQuan <span className="text-muted-foreground">Platform</span></span>
    </Link>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted ? theme === "dark" : true;
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      data-testid="landing-theme-toggle"
      aria-label="Växla tema"
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 text-muted-foreground transition-colors hover:text-foreground"
    >
      <Sun className="h-[18px] w-[18px] rotate-0 scale-100 transition-transform duration-300 dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-[18px] w-[18px] rotate-90 scale-0 transition-transform duration-300 dark:rotate-0 dark:scale-100" />
    </button>
  );
}

function Nav() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "border-b border-border/70 bg-background/85 backdrop-blur-xl" : "border-b border-transparent bg-background/40 backdrop-blur-md"}`} data-testid="landing-nav">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <a key={n.key} href={n.href} className="text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground">{t(`landing.${n.key}`)}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <div className="hidden md:block"><LanguageSwitcher /></div>
          <ThemeToggle />
          <Link to="/login" className="hidden rounded-full px-4 py-2.5 text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:block" data-testid="nav-login">{t("landing.login")}</Link>
          <Link to="/register" data-testid="nav-register" className="hidden sm:block">
            <button className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-primary-foreground shadow-[0_10px_30px_-8px_hsl(var(--primary)/0.7)] transition-all hover:shadow-[0_14px_40px_-6px_hsl(var(--primary)/0.9)] active:scale-[0.98]">{t("login.createAccount")}</button>
          </Link>
          <button className="rounded-lg border border-border p-2.5 lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="nav-menu-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border bg-background/95 px-6 py-5 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <a key={n.key} href={n.href} onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-base font-medium text-muted-foreground hover:bg-muted">{t(`landing.${n.key}`)}</a>
            ))}
            <div className="mt-3"><LanguageSwitcher /></div>
            <div className="mt-3 flex gap-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 rounded-full border border-border py-3 text-center text-sm font-medium" data-testid="mobile-login">{t("landing.login")}</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 rounded-full bg-primary py-3 text-center text-sm font-semibold text-primary-foreground" data-testid="mobile-register">{t("login.createAccount")}</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  const { t } = useTranslation();
  const trust = t("landing.trust", { returnObjects: true });
  return (
    <section id="hem" className="relative mx-auto max-w-6xl px-6 pb-16 pt-24 text-center md:pt-36 lg:px-8">
      {/* Enterprise AI hero backdrop — multi-layered (network, particles, streaks, glow) */}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[1020px] w-screen max-w-[1920px] -translate-x-1/2 overflow-hidden">
        <img src="/brand/hero-bg-dark.jpg" alt="" fetchPriority="high"
          className="hidden h-full w-full object-cover object-center opacity-[0.72] dark:block [mask-image:radial-gradient(ellipse_72%_66%_at_50%_30%,#000_35%,rgba(0,0,0,0.6)_70%,transparent_100%)]" />
        <img src="/brand/hero-bg-light.jpg" alt="" fetchPriority="high"
          className="block h-full w-full object-cover object-center opacity-80 dark:hidden [mask-image:radial-gradient(ellipse_72%_66%_at_50%_30%,#000_35%,rgba(0,0,0,0.6)_70%,transparent_100%)]" />
        <svg className="absolute inset-0 h-full w-full opacity-40 dark:opacity-60" viewBox="0 0 1440 1020" preserveAspectRatio="xMidYMid slice">
          <g fill="none" stroke="hsl(var(--primary) / 0.45)" strokeWidth="1">
            <path d="M60,180 L340,110 L620,250 L900,130 L1200,240 L1400,160" />
            <path d="M120,720 L420,800 L760,680 L1080,820 L1380,700" />
            <path d="M340,110 L420,800" /><path d="M900,130 L1080,820" /><path d="M620,250 L760,680" />
          </g>
          <g fill="hsl(var(--primary) / 0.75)">
            {[[340,110],[620,250],[900,130],[1200,240],[420,800],[760,680],[1080,820]].map(([x,y],i)=>(<circle key={i} cx={x} cy={y} r="3.2" />))}
          </g>
        </svg>
        {[...Array(8)].map((_, i) => (
          <span key={i} className="lp-particle" style={{ left: `${(i * 61) % 100}%`, top: `${(i * 37) % 92}%`, animationDelay: `${(i % 6) * 0.7}s`, animationDuration: `${7 + (i % 5)}s` }} />
        ))}
        <div className="lp-streak absolute left-[22%] top-0 h-full w-px bg-gradient-to-b from-transparent via-primary/25 to-transparent" />
        <div className="lp-streak absolute left-[68%] top-0 h-full w-px bg-gradient-to-b from-transparent via-[#06B6D4]/25 to-transparent" style={{ animationDelay: "3s" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 30%, transparent, hsl(var(--background) / 0.55) 92%)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-transparent to-background" />
      </div>

      <motion.div initial="hidden" animate="show" variants={reveal}>
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-4 py-1.5 text-xs font-medium text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> {t("landing.hero.badge")}
        </span>
      </motion.div>
      <motion.h1 custom={1} initial="hidden" animate="show" variants={reveal} className="mx-auto mt-8 max-w-4xl font-display text-[44px] font-extrabold leading-[1.03] tracking-[-0.03em] sm:text-6xl lg:text-[84px] [text-wrap:balance]">
        {t("landing.hero.titleLine1")}<br />
        <span className="text-muted-foreground">{t("landing.hero.titleLine2")}</span>
        <span style={{ background: "linear-gradient(100deg, hsl(var(--primary)), #7C3AED 55%, #06B6D4)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{t("landing.hero.titleAccent")}</span>
      </motion.h1>
      <motion.p custom={2} initial="hidden" animate="show" variants={reveal} className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl [text-wrap:pretty]">
        {t("landing.hero.subtitle")}
      </motion.p>
      <motion.div custom={3} initial="hidden" animate="show" variants={reveal} className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to="/register" data-testid="hero-register" className="w-full sm:w-auto">
          <button className="group flex w-full items-center justify-center gap-2 rounded-full bg-primary px-9 py-4 text-base font-semibold text-primary-foreground shadow-[0_14px_44px_-10px_hsl(var(--primary)/0.75)] transition-all hover:shadow-[0_18px_56px_-8px_hsl(var(--primary)/0.95)] hover:brightness-[1.05] active:scale-[0.98] sm:w-auto">
            {t("login.createAccount")} <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </Link>
        <Link to="/login" data-testid="hero-login" className="w-full sm:w-auto">
          <button className="w-full rounded-full border border-border bg-background/60 px-9 py-4 text-base font-semibold text-foreground transition-colors hover:bg-muted sm:w-auto">{t("landing.login")}</button>
        </Link>
      </motion.div>
      <motion.ul custom={4} initial="hidden" animate="show" variants={reveal} className="mx-auto mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
        {trust.map((it) => (
          <li key={it} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-emerald-500" /> {it}</li>
        ))}
      </motion.ul>

      <motion.div initial={{ opacity: 0, y: 56 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: 0.3 }} className="relative mt-16 sm:mt-24 lp-perspective">
        {/* frame glow */}
        <div className="lp-shine pointer-events-none absolute -inset-x-16 -top-16 bottom-0 -z-10 rounded-[40px] blur-3xl" style={{ background: "radial-gradient(ellipse at top, hsl(var(--primary) / 0.4), transparent 62%)" }} />
        {/* floating accent cards */}
        <div className="lp-floaty absolute -left-4 -top-6 z-20 hidden items-center gap-2.5 rounded-2xl border border-border bg-card/90 px-4 py-3 shadow-2xl md:flex">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/12 text-emerald-500"><TrendingUp className="h-4 w-4" /></span>
          <div className="text-left"><p className="text-[10px] font-medium text-muted-foreground">Omsättning</p><p className="text-sm font-bold tabular-nums">+22% <span className="font-normal text-muted-foreground">i mån</span></p></div>
        </div>
        <div className="lp-floaty-2 absolute -right-4 top-1/3 z-20 hidden items-center gap-2.5 rounded-2xl border border-border bg-card/90 px-4 py-3 shadow-2xl md:flex">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/12 text-primary"><CreditCard className="h-4 w-4" /></span>
          <div className="text-left"><p className="text-[10px] font-medium text-muted-foreground">Faktura betald</p><p className="text-sm font-bold tabular-nums">4 500 kr</p></div>
        </div>
        <div className="relative mx-auto flex max-w-[1180px] justify-center overflow-hidden rounded-2xl sm:overflow-visible max-sm:max-h-[400px] max-sm:[mask-image:linear-gradient(to_bottom,#000_72%,transparent)]">
          <div className="lp-tilt w-full max-sm:w-[720px] max-sm:min-w-[720px] max-sm:shrink-0">
            <Mockup />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-16 -bottom-6 -z-10 h-44 rounded-[50%] bg-primary/15 blur-3xl dark:bg-primary/25" />
      </motion.div>
    </section>
  );
}

/* ---------- Trust bar directly under hero ---------- */
function TrustBar() {
  const { t } = useTranslation();
  const badges = t("landing.badges", { returnObjects: true });
  return (
    <section className="mx-auto max-w-6xl px-6 pb-6 pt-16 lg:px-8">
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 rounded-2xl border border-border bg-card/50 px-6 py-5">
        {badges.map((b, i) => {
          const Icon = TRUST_ICONS[i] || ShieldCheck;
          return (
            <div key={b} className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground" data-testid={`trust-badge-${i}`}>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" strokeWidth={1.75} /></span>
              {b}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Realistic software mockup (large, hero focal point) ---------- */
function Kpi({ label, value, delta, points }) {
  return (
    <div className="rounded-xl border border-border bg-background/70 p-3.5 text-left">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-end justify-between">
        <span className="font-display text-xl font-bold tabular-nums">{value}</span>
        <span className="flex items-center gap-0.5 text-[11px] font-semibold text-emerald-500"><TrendingUp className="h-3.5 w-3.5" />{delta}</span>
      </div>
      <svg viewBox="0 0 80 22" className="mt-2 h-6 w-full text-primary">
        <polyline fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" points={points} />
      </svg>
    </div>
  );
}

function MiniCard({ icon: Icon, title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-background/70 p-3.5 ${className}`}>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</p>
      {children}
    </div>
  );
}

function Mockup() {
  const nav = [
    [LayoutDashboard, "Översikt", true], [Calendar, "Bokningar"], [Users, "CRM"],
    [Wrench, "Arbetsorder"], [Box, "Lager"], [FileText, "Fakturor"],
    [BarChart3, "Rapporter"], [MessageSquare, "Meddelanden"],
  ];
  const orders = [
    ["AO-2026-0142", "iPhone 14 – skärmbyte", "Pågår", "a"],
    ["AO-2026-0141", "Dell XPS – felsökning", "Klar", "e"],
    ["AO-2026-0139", "Service & underhåll", "Väntar delar", "n"],
  ];
  const invoices = [["F-2026-0311", "12 400 kr", "Betald", "e"], ["F-2026-0310", "3 900 kr", "Skickad", "a"]];
  const badge = (c) => c === "e" ? "bg-emerald-500/12 text-emerald-500" : c === "a" ? "bg-amber-500/12 text-amber-500" : "bg-sky-500/12 text-sky-500";
  const cal = ["09:00 Service", "10:30 Reparation", "13:00 AC-service", "15:30 Däckbyte"];
  const lager = [["Skärm iPhone 14", 3, "low"], ["Batteri Samsung", 24, "ok"], ["Laddkabel USB-C", 6, "low"]];
  const bars = [42, 68, 55, 80, 62, 90, 74];
  return (
    <div className="relative mx-auto max-w-[1180px] rounded-2xl border border-border bg-card/70 p-2.5 shadow-[0_50px_140px_-30px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10 backdrop-blur-xl dark:shadow-[0_60px_180px_-30px_rgba(0,0,0,0.85)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-28 rounded-t-2xl bg-gradient-to-b from-white/10 to-transparent dark:from-white/[0.07]" />
      <div className="relative flex overflow-hidden rounded-xl bg-card">
        {/* sidebar */}
        <aside className="hidden w-52 shrink-0 flex-col gap-0.5 border-r border-border p-3.5 sm:flex">
          <div className="mb-3.5 flex items-center gap-2 px-1">
            <span className="h-6 w-6 overflow-hidden rounded"><BrandLogo className="h-full w-full" /></span>
            <span className="font-display text-xs font-bold">ArcQuan</span>
          </div>
          {nav.map(([Icon, l, a]) => (
            <div key={l} className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium ${a ? "bg-primary/12 text-primary" : "text-muted-foreground"}`}>
              <Icon className="h-4 w-4" strokeWidth={1.75} /> {l}
            </div>
          ))}
          <div className="mt-auto flex items-center gap-2 rounded-md border border-border px-3 py-2">
            <span className="h-7 w-7 rounded-full bg-gradient-to-br from-primary to-[#7C3AED]" />
            <div className="min-w-0"><p className="truncate text-[11px] font-semibold">Erik Nordström</p><p className="truncate text-[9px] text-muted-foreground">Admin</p></div>
          </div>
        </aside>

        {/* content */}
        <div className="min-w-0 flex-1 p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-[11px] text-muted-foreground"><Search className="h-3.5 w-3.5" /> Sök kund, ärende…</div>
            <div className="flex items-center gap-3">
              <span className="relative"><Bell className="h-4 w-4 text-muted-foreground" /><span className="lp-live absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" /></span>
              <span className="h-7 w-7 rounded-full bg-gradient-to-br from-primary to-[#7C3AED]" />
            </div>
          </div>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-display text-base font-bold">Översikt</p>
            <span className="rounded-md bg-muted px-2.5 py-1 text-[10px] font-medium text-muted-foreground">Senaste 30 dagarna</span>
          </div>
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <Kpi label="Aktiva kunder" value="1 243" delta="+15%" points="0,18 13,15 26,16 40,10 53,12 66,6 80,4" />
            <Kpi label="Bokningar" value="24" delta="+12%" points="0,14 13,16 26,10 40,12 53,7 66,9 80,5" />
            <Kpi label="Öppna ärenden" value="18" delta="+8%" points="0,12 13,13 26,9 40,11 53,8 66,6 80,7" />
            <Kpi label="Omsättning" value="125k" delta="+22%" points="0,20 13,16 26,17 40,11 53,9 66,6 80,3" />
          </div>

          <div className="mt-2.5 grid grid-cols-1 gap-2.5 lg:grid-cols-5">
            {/* revenue chart */}
            <div className="rounded-xl border border-border bg-background/70 p-4 lg:col-span-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-[11px] font-semibold text-muted-foreground">Omsättning · 30 dagar</p>
                <span className="text-[10px] font-semibold text-emerald-500">+22%</span>
              </div>
              <svg viewBox="0 0 220 84" className="h-24 w-full text-primary">
                <defs>
                  <linearGradient id="lp-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary) / 0.3)" />
                    <stop offset="100%" stopColor="hsl(var(--primary) / 0)" />
                  </linearGradient>
                </defs>
                <polygon fill="url(#lp-area)" points="0,66 28,56 56,60 84,44 112,48 140,30 168,34 196,16 220,12 220,84 0,84" />
                <polyline fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" points="0,66 28,56 56,60 84,44 112,48 140,30 168,34 196,16 220,12" />
              </svg>
            </div>
            {/* calendar widget */}
            <MiniCard icon={Calendar} title="Idag" className="lg:col-span-2">
              {cal.map((c) => (
                <div key={c} className="flex items-center gap-2 py-1 text-[11px] font-medium text-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {c}
                </div>
              ))}
            </MiniCard>
          </div>

          <div className="mt-2.5 grid grid-cols-1 gap-2.5 lg:grid-cols-5">
            {/* work orders */}
            <div className="rounded-xl border border-border bg-background/70 p-4 lg:col-span-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><Wrench className="h-3.5 w-3.5" /> Senaste arbetsorder</p>
              {orders.map(([no, desc, st, c]) => (
                <div key={no} className="flex items-center justify-between border-t border-border/60 py-2 first:border-t-0">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="font-mono text-[10px] text-muted-foreground">{no}</span>
                    <span className="truncate text-[11px] font-medium">{desc}</span>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-[9px] font-semibold ${badge(c)}`}>{st}</span>
                </div>
              ))}
            </div>
            {/* invoices */}
            <MiniCard icon={FileText} title="Fakturor" className="lg:col-span-2">
              {invoices.map(([no, amt, st, c]) => (
                <div key={no} className="flex items-center justify-between py-1.5">
                  <span className="font-mono text-[10px] text-muted-foreground">{no}</span>
                  <span className="text-[11px] font-semibold tabular-nums">{amt}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${badge(c)}`}>{st}</span>
                </div>
              ))}
            </MiniCard>
          </div>

          <div className="mt-2.5 hidden grid-cols-1 gap-2.5 sm:grid lg:grid-cols-5">
            {/* inventory low stock */}
            <MiniCard icon={Box} title="Lager" className="lg:col-span-2">
              {lager.map(([name, qty, s]) => (
                <div key={name} className="flex items-center justify-between py-1.5 text-[11px]">
                  <span className="truncate text-foreground">{name}</span>
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-[9px] font-semibold ${s === "low" ? "bg-destructive/12 text-destructive" : "bg-emerald-500/12 text-emerald-500"}`}>{qty} st</span>
                </div>
              ))}
            </MiniCard>
            {/* reports bar chart */}
            <div className="rounded-xl border border-border bg-background/70 p-4 lg:col-span-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><BarChart3 className="h-3.5 w-3.5" /> Rapporter · veckans intäkt</p>
              <div className="flex h-16 items-end gap-2">
                {bars.map((h, i) => (
                  <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary/40 to-primary" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Clients marquee ---------- */
function Clients() {
  const { t } = useTranslation();
  const sectors = t("landing.sectors", { returnObjects: true });
  const list = [...sectors, ...sectors];
  return (
    <section className="mx-auto max-w-7xl px-6 py-14 text-center lg:px-8">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{t("landing.socialProof")}</p>
      <div className="lp-marquee-wrap relative mt-7 overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]">
        <div className="lp-marquee flex w-max items-center gap-12">
          {list.map((c, i) => (
            <span key={c + i} className="whitespace-nowrap font-display text-base font-bold tracking-wide text-muted-foreground/50 transition-colors hover:text-foreground">{c}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHead({ overline, title, subtitle }) {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={reveal} className="mx-auto max-w-2xl text-center">
      {overline && <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">{overline}</p>}
      <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{title}</h2>
      {subtitle && <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>}
    </motion.div>
  );
}

function Features() {
  const { t } = useTranslation();
  const items = [...t("landing.features.items", { returnObjects: true }), ...t("landing.featuresExtra", { returnObjects: true })];
  return (
    <section id="funktioner" className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8">
      <div className="absolute inset-0 -z-10 lp-grid-soft" />
      <SectionHead overline={t("landing.nav.features")} title={t("landing.features.title")} subtitle={t("landing.features.subtitle")} />
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((f, i) => {
          const Icon = FEATURE_ICONS[i] || Calendar;
          return (
            <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
              className="group rounded-2xl border border-border bg-card/70 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_20px_60px_-24px_hsl(var(--primary)/0.5)]" data-testid={`feature-${i}`}>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/12 text-primary transition-transform duration-300 group-hover:scale-105"><Icon className="h-6 w-6" strokeWidth={1.75} /></div>
              <h3 className="font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function WhyUs() {
  const { t } = useTranslation();
  const items = t("landing.why.items", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
      <SectionHead overline="Plattform" title={t("landing.why.title")} subtitle={t("landing.why.subtitle")} />
      <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
        {items.map((w, i) => {
          const Icon = WHY_ICONS[i] || Cloud;
          return (
            <motion.div key={w.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal} className="group rounded-2xl border border-transparent p-6 transition-all hover:border-border hover:bg-card/50" data-testid={`why-${i}`}>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-card text-primary transition-transform group-hover:scale-105"><Icon className="h-5 w-5" strokeWidth={1.75} /></div>
              <h3 className="font-display text-lg font-semibold">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const steps = [...t("landing.steps.items", { returnObjects: true }), ...t("landing.stepsExtra", { returnObjects: true })];
  return (
    <section id="sa-fungerar" className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
      <SectionHead overline={t("landing.steps.title")} title={t("landing.steps.title")} subtitle={t("landing.steps.subtitle")} />
      <div className="relative mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className="relative rounded-2xl border border-border bg-card/70 p-6 transition-all hover:-translate-y-1 hover:border-primary/30" data-testid={`step-${i}`}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/12 font-display text-sm font-bold text-primary">{i + 1}</span>
            <h3 className="mt-3 font-display text-base font-semibold">{s.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const { t } = useTranslation();
  const items = t("landing.testimonials.items", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
      <SectionHead overline={t("landing.testimonials.demo")} title={t("landing.testimonials.title")} subtitle={t("landing.testimonials.subtitle")} />
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {items.map((r, i) => (
          <motion.figure key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className="rounded-2xl border border-border bg-card/70 p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-30px_hsl(var(--primary)/0.5)]" data-testid={`testimonial-${i}`}>
            <div className="mb-3 flex gap-0.5 text-amber-400">{[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}</div>
            <blockquote className="text-sm leading-relaxed text-foreground">"{r.quote}"</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-[#7C3AED]/20 text-primary ring-1 ring-border"><Building2 className="h-5 w-5" strokeWidth={1.75} /></span>
              <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs text-muted-foreground">{r.company}</p></div>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  const { t } = useTranslation();
  const plans = t("landing.pricing.plans", { returnObjects: true });
  const prices = ["299", "699", "1299", null];
  return (
    <section id="priser" className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8">
      <div className="absolute inset-0 -z-10 lp-grid-soft" />
      <SectionHead overline={t("landing.nav.pricing")} title={t("landing.pricing.title")} subtitle={t("landing.pricing.subtitle")} />
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p, i) => {
          const price = prices[i];
          const highlight = i === 2;
          return (
            <div key={p.name} className={`relative flex flex-col rounded-2xl border p-7 transition-all ${highlight ? "border-primary bg-card shadow-[0_28px_90px_-28px_hsl(var(--primary)/0.65)] xl:-translate-y-2" : "border-border bg-card/60 hover:-translate-y-1 hover:border-primary/30"}`} data-testid={`pricing-${p.name.toLowerCase()}`}>
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-[0_6px_20px_-6px_hsl(var(--primary)/0.8)]">{t("landing.pricing.mostPopular")}</span>}
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                {price ? <><span className="font-display text-4xl font-bold tabular-nums">{price}</span><span className="text-sm text-muted-foreground">{t("landing.pricing.perMonth")}</span></> : <span className="font-display text-3xl font-bold">{t("landing.pricing.contactUs")}</span>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{p.users}</p>
              <ul className="mt-6 flex flex-1 flex-col gap-3">
                {p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>)}
              </ul>
              <Link to={price ? "/register" : "#kontakt"} className="mt-7" data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                <button className={`w-full rounded-full py-3 text-sm font-semibold transition-all active:scale-[0.98] ${highlight ? "bg-primary text-primary-foreground shadow-[0_8px_28px_-8px_hsl(var(--primary)/0.7)] hover:shadow-[0_12px_40px_-6px_hsl(var(--primary)/0.9)]" : "border border-border bg-background hover:bg-muted"}`}>
                  {price ? t("login.createAccount") : t("landing.pricing.contactUs")}
                </button>
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useTranslation();
  const items = [...t("landing.faq.items", { returnObjects: true }), ...t("landing.faqExtra", { returnObjects: true })];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="mx-auto max-w-3xl px-6 py-24 lg:px-8">
      <SectionHead overline="FAQ" title={t("landing.faq.title")} />
      <div className="mt-12 flex flex-col gap-3">
        {items.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-xl border border-border bg-card/70 transition-colors hover:border-primary/30" data-testid={`faq-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left" data-testid={`faq-toggle-${i}`}>
              <span className="font-medium">{item.q}</span>
              <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ${open === i ? "rotate-180 text-primary" : ""}`} />
            </button>
            {open === i && <p className="px-6 pb-5 text-sm leading-relaxed text-muted-foreground">{item.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Final CTA band ---------- */
function CtaBand() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
        className="relative overflow-hidden rounded-3xl border border-primary/30 bg-card/60 px-8 py-16 text-center backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: "radial-gradient(ellipse at center, hsl(var(--primary) / 0.22), transparent 70%)" }} />
        <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("landing.hero.titleLine1")} {t("landing.hero.titleLine2")}{t("landing.hero.titleAccent")}</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">{t("landing.pricing.subtitle")}</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register" data-testid="cta-register">
            <button className="group flex items-center justify-center gap-2 rounded-full bg-primary px-9 py-4 text-base font-semibold text-primary-foreground shadow-[0_14px_44px_-10px_hsl(var(--primary)/0.75)] transition-all hover:shadow-[0_18px_56px_-8px_hsl(var(--primary)/0.95)] active:scale-[0.98]">
              {t("login.createAccount")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
          <Link to="/login" data-testid="cta-login">
            <button className="rounded-full border border-border bg-background/60 px-9 py-4 text-base font-semibold transition-colors hover:bg-muted">{t("landing.login")}</button>
          </Link>
        </div>
        <p className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {t("landing.trust", { returnObjects: true })[0]}</p>
      </motion.div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  const cols = t("landing.footer.columns", { returnObjects: true });
  return (
    <footer id="kontakt" className="relative z-10 border-t border-border bg-card/40 py-16">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 md:grid-cols-6 lg:px-8">
        <div className="col-span-2">
          <Logo footer />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">{t("landing.footer.tagline")}</p>
        </div>
        {cols.map((col) => (
          <div key={col.title}>
            <p className="mb-4 text-sm font-semibold">{col.title}</p>
            <ul className="flex flex-col gap-2.5">
              {col.items.map((it) => <li key={it}><a href="#" className="text-sm text-muted-foreground transition-colors hover:text-foreground">{it}</a></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center justify-between gap-3 border-t border-border px-6 pt-8 text-sm text-muted-foreground sm:flex-row lg:px-8">
        <span>{t("landing.footer.rights")}</span>
        <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {t("landing.footer.secure")}</span>
      </div>
    </footer>
  );
}
